import type {
  CounterpartyRefOrDetail,
  CounterpartyRole,
  ShipmentInput,
  VehicleRefOrDetail,
} from "@deca/shared-types";

/**
 * Lógica del formulario manual de creación de DeCA (CLAUDE.md Fase 4).
 *
 * Se mantiene como funciones puras —sin React— por un motivo concreto: la
 * regla del bloque ADR es una regla **normativa** y tiene que poder probarse
 * sola. El error clásico es mandar `adr` relleno con `esADR = false`, o
 * mandarlo vacío con `esADR = true`; ambas cosas se rechazan aquí, en cliente
 * y con el mismo criterio que aplica el esquema Zod del servidor.
 *
 * El formulario llama al mismo `POST /api/v1/internal/deca` que usa la API
 * pública: no hay una segunda vía de creación (CLAUDE.md, regla transversal).
 */

export interface ShipmentDraft {
  origen: string;
  destino: string;
  naturalezaMercancia: string;
  bultos: string;
  pesoNetoKg: string;
  pesoBrutoKg: string;
  esADR: boolean;
  adr: {
    numeroOnu: string;
    denominacion: string;
    clase: string;
    grupoEmbalaje: string;
    restriccionTunel: string;
  };
  /** Índice en la lista de contrapartes del catálogo; "" = sin destinatario. */
  destinatarioId: string;
  /** Notas adicionales del envío (campo libre, opcional). */
  observaciones: string;
}

/** Selección de una parte: "del catálogo" o "nueva, escrita a mano". */
export interface PartyField {
  mode: "catalog" | "manual";
  counterpartyId: string;
  detail: {
    name: string;
    nif: string;
    address: string;
    email: string;
    phone: string;
    defaultRole: CounterpartyRole;
  };
}

export interface VehicleField {
  mode: "catalog" | "manual" | "none";
  vehicleId: string;
  detail: {
    plateTractor: string;
    mmaTractorKg: string;
    plateTrailer: string;
    mmaTrailerKg: string;
    vehicleType: string;
  };
}

export interface DecaFormState {
  cargadorContractual: PartyField;
  transportistaEfectivo: PartyField;
  operadorIntermedio: PartyField;
  vehiculo: VehicleField;
  shipments: ShipmentDraft[];
  driverId: string;
  /** Fecha y hora de inicio real del servicio — el gestor la escribe, independiente de cuándo se crea el registro. */
  transportStartDate: string;
  transportEndDate: string;
}

export const EMPTY_ADR = {
  numeroOnu: "",
  denominacion: "",
  clase: "",
  grupoEmbalaje: "",
  restriccionTunel: "",
} as const;

export function emptyParty(role: CounterpartyRole): PartyField {
  return {
    mode: "catalog",
    counterpartyId: "",
    detail: { name: "", nif: "", address: "", email: "", phone: "", defaultRole: role },
  };
}

export function emptyVehicle(): VehicleField {
  return {
    mode: "none",
    vehicleId: "",
    detail: {
      plateTractor: "",
      mmaTractorKg: "",
      plateTrailer: "",
      mmaTrailerKg: "",
      vehicleType: "",
    },
  };
}

export function emptyShipment(): ShipmentDraft {
  return {
    origen: "",
    destino: "",
    naturalezaMercancia: "",
    bultos: "",
    pesoNetoKg: "",
    pesoBrutoKg: "",
    esADR: false,
    adr: { ...EMPTY_ADR },
    destinatarioId: "",
    observaciones: "",
  };
}

export function emptyDecaForm(): DecaFormState {
  return {
    cargadorContractual: emptyParty("CARGADOR_CONTRACTUAL"),
    transportistaEfectivo: emptyParty("TRANSPORTISTA_EFECTIVO"),
    operadorIntermedio: emptyParty("OPERADOR_INTERMEDIO"),
    vehiculo: emptyVehicle(),
    shipments: [emptyShipment()],
    driverId: "",
    transportStartDate: "",
    transportEndDate: "",
  };
}

/** Convierte "" en null y un número escrito a mano en número (o null). */
function optionalText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Traduce una parte del formulario al contrato de la API. Exactamente una
 * rama queda presente (`counterpartyId` **o** `detail`), como exige el
 * `refine` de `counterpartyRefOrDetailSchema`.
 */
export function partyToRefOrDetail(field: PartyField): CounterpartyRefOrDetail {
  if (field.mode === "catalog" && field.counterpartyId) {
    return { counterpartyId: field.counterpartyId };
  }
  const detail: NonNullable<CounterpartyRefOrDetail["detail"]> = {
    name: field.detail.name.trim(),
    nif: field.detail.nif.trim().toUpperCase(),
    address: field.detail.address.trim(),
    defaultRole: field.detail.defaultRole,
  };
  const email = optionalText(field.detail.email);
  const phone = optionalText(field.detail.phone);
  if (email) detail.email = email;
  if (phone) detail.phone = phone;
  return { detail };
}

export function vehicleToRefOrDetail(field: VehicleField): VehicleRefOrDetail | null {
  if (field.mode === "none") return null;
  if (field.mode === "catalog" && field.vehicleId) return { vehicleId: field.vehicleId };

  const detail: NonNullable<VehicleRefOrDetail["detail"]> = {
    plateTractor: field.detail.plateTractor.trim().toUpperCase(),
  };
  const mmaTractorKg = optionalNumber(field.detail.mmaTractorKg);
  const plateTrailer = optionalText(field.detail.plateTrailer);
  const mmaTrailerKg = optionalNumber(field.detail.mmaTrailerKg);
  const vehicleType = optionalText(field.detail.vehicleType);
  if (mmaTractorKg !== null) detail.mmaTractorKg = mmaTractorKg;
  if (plateTrailer) detail.plateTrailer = plateTrailer.toUpperCase();
  if (mmaTrailerKg !== null) detail.mmaTrailerKg = mmaTrailerKg;
  if (vehicleType) detail.vehicleType = vehicleType;
  return { detail };
}

/**
 * Traduce un envío. El bloque ADR **solo** se incluye cuando `esADR` es
 * verdadero (SKILL.md sección 8): si el usuario rellenó los campos ADR y
 * luego desmarcó la casilla, esos valores se descartan en lugar de viajar
 * como ruido al snapshot.
 */
export function shipmentToInput(draft: ShipmentDraft): ShipmentInput {
  const pesoNetoKg = optionalNumber(draft.pesoNetoKg) ?? 0;

  const obs = optionalText(draft.observaciones);

  const shipment: ShipmentInput = {
    origen: draft.origen.trim(),
    destino: draft.destino.trim(),
    naturalezaMercancia: draft.naturalezaMercancia.trim(),
    bultos: optionalText(draft.bultos),
    pesoNetoKg,
    pesoBrutoKg: optionalNumber(draft.pesoBrutoKg),
    esADR: draft.esADR,
    adr: null,
    ...(obs ? { observaciones: obs } : {}),
  };

  if (draft.destinatarioId) {
    shipment.destinatario = { counterpartyId: draft.destinatarioId };
  }

  if (draft.esADR) {
    shipment.adr = {
      numeroOnu: optionalText(draft.adr.numeroOnu),
      denominacion: optionalText(draft.adr.denominacion),
      clase: optionalText(draft.adr.clase),
      grupoEmbalaje: optionalText(draft.adr.grupoEmbalaje),
      restriccionTunel: optionalText(draft.adr.restriccionTunel),
    };
  }

  return shipment;
}

export interface DecaFormPayload {
  cargadorContractual: CounterpartyRefOrDetail;
  transportistaEfectivo: CounterpartyRefOrDetail;
  operadorIntermedio?: CounterpartyRefOrDetail;
  vehiculo?: VehicleRefOrDetail | null;
  shipments: ShipmentInput[];
  driverId?: string;
  transportStartDate?: string;
  transportEndDate?: string;
}

export function buildCreateDecaPayload(form: DecaFormState): DecaFormPayload {
  const payload: DecaFormPayload = {
    cargadorContractual: partyToRefOrDetail(form.cargadorContractual),
    transportistaEfectivo: partyToRefOrDetail(form.transportistaEfectivo),
    shipments: form.shipments.map(shipmentToInput),
    vehiculo: vehicleToRefOrDetail(form.vehiculo),
  };

  // El operador intermedio es opcional: si no se ha indicado nada, se omite
  // en lugar de mandar una parte vacía que la API rechazaría.
  const operador = form.operadorIntermedio;
  const operadorTouched =
    (operador.mode === "catalog" && operador.counterpartyId !== "") ||
    (operador.mode === "manual" && operador.detail.name.trim() !== "");
  if (operadorTouched) payload.operadorIntermedio = partyToRefOrDetail(operador);

  if (form.driverId) payload.driverId = form.driverId;
  if (form.transportStartDate) {
    // El inicio del transporte se envía tal cual como ISO: el gestor ya eligió
    // fecha y hora en el campo datetime-local (p. ej. "2026-10-03T08:00").
    payload.transportStartDate = new Date(form.transportStartDate).toISOString();
  }
  if (form.transportEndDate) {
    // El fin de transporte se guarda como instante ISO: es la fecha de la que
    // dependen los 7 días naturales hasta desactivar la descarga.
    payload.transportEndDate = new Date(`${form.transportEndDate}T23:59:59`).toISOString();
  }

  return payload;
}

/**
 * Validación en cliente, espejo de la del servidor. Se hace aquí para dar un
 * mensaje junto al campo en vez de un 400 genérico tras enviar; la autoridad
 * sigue siendo el esquema Zod de la API.
 */
export function validateDecaForm(form: DecaFormState): string[] {
  const problems: string[] = [];

  const checkParty = (field: PartyField, label: string): void => {
    if (field.mode === "catalog") {
      if (!field.counterpartyId) problems.push(`${label}: selecciona una contraparte del catálogo`);
      return;
    }
    if (!field.detail.name.trim()) problems.push(`${label}: falta el nombre o razón social`);
    if (!field.detail.nif.trim()) problems.push(`${label}: falta el NIF`);
    if (!field.detail.address.trim()) problems.push(`${label}: falta la dirección`);
  };

  checkParty(form.cargadorContractual, "Cargador contractual");
  checkParty(form.transportistaEfectivo, "Transportista efectivo");

  if (form.vehiculo.mode === "manual" && !form.vehiculo.detail.plateTractor.trim()) {
    problems.push("Vehículo: falta la matrícula de la tractora");
  }

  if (form.shipments.length === 0) problems.push("Añade al menos un envío");

  form.shipments.forEach((shipment, index) => {
    const position = `Envío ${index + 1}`;
    if (!shipment.origen.trim()) problems.push(`${position}: falta el origen`);
    if (!shipment.destino.trim()) problems.push(`${position}: falta el destino`);
    if (!shipment.naturalezaMercancia.trim()) {
      problems.push(`${position}: falta la naturaleza de la mercancía`);
    }
    const peso = optionalNumber(shipment.pesoNetoKg);
    if (peso === null || peso <= 0) problems.push(`${position}: el peso neto debe ser mayor que 0`);

    if (shipment.esADR) {
      if (!shipment.adr.numeroOnu.trim()) {
        problems.push(`${position}: es ADR, falta el número ONU`);
      }
      if (!shipment.adr.denominacion.trim()) {
        problems.push(`${position}: es ADR, falta la denominación de la mercancía`);
      }
      if (!shipment.adr.clase.trim()) {
        problems.push(`${position}: es ADR, falta la clase de peligro`);
      }
    }
  });

  return problems;
}
